import { useState, useEffect, useMemo, useCallback, MouseEvent } from "react";
import PageHeader from "../../../components/Page/PageHeader";
import { useGetDatasetsDatasetGet, useGetDatasetTagsDatasetTagsGet } from "../../../api/generated/fastAPI";
import { DatasetOutput, DatasetListResponse, GetDatasetsDatasetGetParams } from "../../../api/models";
import FilterBar, { FilterBarType } from "../../../components/Filtering/FilterBar";
import useDataParams, { GetParamsTypes } from "../../../hooks/useDataParams";
import useMessage from "../../../hooks/useMessage";
import GenerateDatasetModal from "../components/GenerateDatasetModal";
import SearchBar from "../../../components/Filtering/SearchBar";
import ColumnsButton from "../../../components/Buttons/ColumnsButton";
import { DropdownOption } from "../../../components/Menus&Dropdowns/Dropdown";
import { TableData, TableHeader } from "../../../components/Table/DataTable";
import UpdatedPagination from "../../../components/Filtering/UpdatedPagination";
import SnackBar from "../../../components/Alerts/SnackBar";
import { useNavigate } from "react-router-dom";
import DeleteModal, { DeleteItemType } from "../../../components/Popups/DeleteModal";
import CurateDatasetModal from "../components/CurateDatasetModal";
import TrainModelModal from "../../Models/components/TrainModelModal";
import BatchPredictionModal from "../../Resultsets/components/BatchPredictionModal";
import NewPageTabs from "../../../components/Tabs/NewPageTabs";
import { exportFileExtensions } from "../../../utils/supplementalData";
import NestedDropdown from "../../../components/Menus&Dropdowns/NestedDropdown";
import useDelete from "../../../hooks/endpoints/useDelete";
import { useAccess } from "../../../access/useAccess";
import { ac_access, demo_access } from "../../../access/predefinedConditions";
import useDownload from "../../../hooks/endpoints/useDownload";
import VirtualizedDataTable from "../../../components/Table/VirtualizedDataTable";
import { customInstance } from "../../../api/custom-instance";


const datasetHeaders: TableHeader[] = [
    {
        label: 'Name',
        value: 'name',
        isShown: true,
        alignment: 'left',
    },
    {
        label: 'Records',
        value: 'records',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Tags',
        value: 'tags',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Created By',
        value: 'created_by',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Owned By',
        value: 'owner',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Date Created',
        value: 'creation_date',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Date Updated',
        value: 'updated_date',
        isShown: true,
        alignment: 'center',
    },
];


const defaultFilters: FilterBarType[] = [
    {
        label: 'Created Date Range',
        param: 'start_date',
        type: 'date-range',
        isDefault: true,
        groupKey: 'date-range',
    },
    {
        label: 'Created Date Range',
        param: 'end_date',
        type: 'date-range',
        isDefault: true,
        groupKey: 'date-range',
    },
    {
        label: 'Tags',
        param: 'tag_groups',
        type: 'tags',
        isDefault: true,
    },
    {
        label: 'Created By',
        param: 'created_by',
        type: 'users',
        isDefault: true,
    },
    {
        label: 'Owner',
        param: 'owner',
        type: 'users',
        isDefault: true,
    },
]


export default function Datasets() {
    const { canAccess } = useAccess();
    const { downloadDataset } = useDownload();
    const hasAcAccess = canAccess({or: ac_access});

    // Only assay central can see single-class and continuous value tabs unless admin and cpi_employee
    let tabs = [];
    let default_dataset_type = 'structures-only';
    if (hasAcAccess) {
        tabs.push('Single-Class', 'Continuous-Value');
        default_dataset_type = 'single-class-label';
    };
    tabs.push('Structure Only');

    const defaultParams = {
        name: null,
        created_by: null,
        owner: null,
        start_date: null,
        end_date: null,
        tag_groups: null,
        dataset_type: default_dataset_type === 'structures-only' ? 'structures-only' : 'single-class-label',
        grouped: hasAcAccess,
        sort_by: 'creation_date',
        sort_order: 'desc',
        limit: 10,
        offset: 0,
        include_airflow_metadata: false,
    } as GetDatasetsDatasetGetParams;

    const getDatasetTags = useGetDatasetTagsDatasetTagsGet();

    const {
        open,
        message,
        isSuccess,
        setOpen,
        setPopupMessage
    } = useMessage();

    const {
        params,
        setParams,
        search,
        sort,
        filters,
        setPaginationLimit,
        setPaginationOffset
    } = useDataParams({
        defaultParams,
        defaultFilters,
        tagsData: getDatasetTags.data as unknown as { [key: string]: string[] },
    })

   
    const getDatasetMutation = useGetDatasetsDatasetGet(params as GetParamsTypes);

    const [ count, setCount ] = useState(0);
    const [ selectedItems, setSelectedItems ] = useState<(string | number)[]>([]);
    const [ tableHeaders, setTableHeaders ] = useState<TableHeader[]>(datasetHeaders);
    const [ datasets, setDatasets ] = useState<TableData[]>([]);
    const [ ds, setDs ] = useState<DatasetOutput[]>([]);
    const [ actionDropdownOptions, setActionDropdownOptions ] = useState<DropdownOption[]>([]);
    const [ selectedIndex, setSelectedIndex ] = useState(0);

    const [openTrainModelId, setOpenTrainModelId] = useState<string | null>(null);
    const [openBatchPredictionId, setOpenBatchPredictionId] = useState<string | null>(null);

    // Version expansion state
    const [expandedRows, setExpandedRows] = useState<Set<string | number>>(new Set());
    const [versionCache, setVersionCache] = useState<Record<string, DatasetOutput[]>>({});
    const [loadingVersions, setLoadingVersions] = useState<Set<string | number>>(new Set());

    const isGrouped = !!(params as any).grouped;

    // Fix: On megatox/megatrans/megaache, roles load async so canAccess may flip
    // from false→true after first render. Sync dataset_type and grouped when that happens.
    useEffect(() => {
        const correctType = hasAcAccess ? 'single-class-label' : 'structures-only';
        setParams(prev => {
            const p = prev as GetDatasetsDatasetGetParams;
            const needsTypeUpdate = p.dataset_type !== correctType && selectedIndex === 0;
            const needsGroupedUpdate = (prev as any).grouped !== hasAcAccess;
            if (needsTypeUpdate || needsGroupedUpdate) {
                return { 
                    ...prev, 
                    ...(needsTypeUpdate ? { dataset_type: correctType, offset: 0 } : {}),
                    grouped: hasAcAccess,
                };
            }
            return prev;
        });
    }, [hasAcAccess]);


    const navigate = useNavigate();

    const refetchDatasets = () => {
        getDatasetMutation.refetch();
    }

    const { deleteDatasets } = useDelete({ setPopupMessage, refetchFn: refetchDatasets });

    // Build dataset fields from a DatasetOutput (shared between group headers and version rows)
    const buildDatasetFields = useCallback((ds: DatasetOutput) => [
        {
            associatedColumn: 'name',
            value: ds.name!,
            alignment: 'left' as const,
            type: 'text' as const,
            action: (e:MouseEvent<HTMLButtonElement>) => navigate(`/datasets/${ds.file_hash!}`),
        },
        {
            associatedColumn: 'records',
            value: ds.dataset_metadata?.records_number ? ds.dataset_metadata.records_number.toString() : 'No Value',
            alignment: 'center' as const,
            type: 'text' as const,
        },
        {
            associatedColumn: 'tags',
            value: (ds.file_metadata?.tags ?? []) as string[],
            alignment: 'center' as const,
            type: 'tags' as const,
        },
        {
            associatedColumn: 'created_by',
            value: ds.created_by ? ds.created_by : 'No Value',
            alignment: 'center' as const,
            type: 'text' as const,
        },
        {
            associatedColumn: 'owner',
            value: ds.owner ? ds.owner : 'No Value',
            alignment: 'center' as const,
            type: 'text' as const,
        },
        {
            associatedColumn: 'creation_date',
            value: ds.creation_date ? new Date(ds.creation_date!).toLocaleString('en-US', {timeZone: 'EST'}) : 'No Value',
            alignment: 'center' as const,
            type: 'text' as const,
        },
        {
            associatedColumn: 'updated_date',
            value: ds.updated_date ? new Date(ds.updated_date!).toLocaleString('en-US', {timeZone: 'EST'}) : 'No Value',
            alignment: 'center' as const,
            type: 'text' as const,
        },
    ], [navigate]);

    useEffect(() => {
        if (getDatasetMutation.data) {
            const response = getDatasetMutation.data as unknown as DatasetListResponse;
            const data = response.datasets ?? [];
            setCount(response.count ?? 0);
            setDs(data as unknown as DatasetOutput[]);

            const items = data.map(ds => {
                const groupCount = (ds as any).group_count as number | undefined;
                return {
                    id: ds.file_hash!,
                    fields: buildDatasetFields(ds as unknown as DatasetOutput),
                    options: injectOptions(ds as unknown as DatasetOutput),
                    isGroupHeader: hasAcAccess && (groupCount ?? 0) > 1,
                    groupCount: groupCount ?? undefined,
                };
            });

            setDatasets(items);
        }

        //eslint-disable-next-line
    }, [getDatasetMutation.data]);

    // Flatten datasets with expanded version rows
    const flattenedTableData = useMemo(() => {
        if (!isGrouped || expandedRows.size === 0) return datasets;

        const items: TableData[] = [];
        for (const row of datasets) {
            items.push(row);

            if (row.isGroupHeader && expandedRows.has(row.id)) {
                const dataset = ds.find(d => d.file_hash === row.id);
                if (!dataset) continue;

                const cacheKey = `${dataset.owner}::${dataset.name}`;
                const versions = versionCache[cacheKey];

                if (versions) {
                    for (const v of versions) {
                        if (v.file_hash === dataset.file_hash) continue; // Skip the group representative
                        items.push({
                            id: v.file_hash!,
                            fields: buildDatasetFields(v),
                            options: injectOptions(v),
                            isVersionRow: true,
                        });
                    }
                } else if (loadingVersions.has(row.id)) {
                    items.push({
                        id: `loading-${row.id}`,
                        fields: [{
                            associatedColumn: 'name',
                            value: 'Loading versions...',
                            alignment: 'left' as const,
                            type: 'text' as const,
                        }],
                        isVersionRow: true,
                    });
                }
            }
        }
        return items;
    }, [datasets, expandedRows, versionCache, loadingVersions, ds, isGrouped, buildDatasetFields]);

    // Handle expand/collapse of version groups
    const handleToggleExpand = useCallback(async (id: string | number) => {
        if (expandedRows.has(id)) {
            setExpandedRows(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
            return;
        }

        // Expand
        setExpandedRows(prev => new Set(prev).add(id));

        const dataset = ds.find(d => d.file_hash === id);
        if (!dataset) return;

        const cacheKey = `${dataset.owner}::${dataset.name}`;
        if (versionCache[cacheKey]) return; // Already cached

        // Fetch versions
        setLoadingVersions(prev => new Set(prev).add(id));
        try {
            const response = await customInstance({
                url: '/dataset/versions',
                method: 'GET',
                params: {
                    owner: dataset.owner,
                    name: dataset.name,
                    dataset_type: (params as any).dataset_type,
                    include_airflow_metadata: false,
                },
            }) as unknown as DatasetListResponse;

            setVersionCache(prev => ({
                ...prev,
                [cacheKey]: (response.datasets ?? []) as unknown as DatasetOutput[],
            }));
        } catch (err) {
            console.error('Failed to fetch dataset versions:', err);
            setExpandedRows(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        } finally {
            setLoadingVersions(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    }, [expandedRows, ds, versionCache, params]);


    function injectOptions(ds: DatasetOutput) {
        const id = ds.file_hash!;
        const name = ds.name!;

        let options: DropdownOption[] = [
            {
                label: 'Curate Dataset',
                btnComp: (
                    <CurateDatasetModal 
                        selectedDatasetId={id}
                        setPopupMessage={setPopupMessage} 
                    />
                )
            },
        ];
        if (canAccess({or: ac_access})) options.push(
            {
                label: 'Train Model',
                action: () => setOpenTrainModelId(id), // Only trigger open
            }
        );
        options.push(
            {
                label: 'Run Prediction',
                action: () => setOpenBatchPredictionId(id), // Only trigger open
            },
            {
                label: 'Export',
                action: (e:MouseEvent<HTMLButtonElement>) => {},
                moreOptions: exportFileExtensions.map(ext => (
                    {
                        label: ext.label,
                        action: async (e: MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                            try {
                                await downloadDataset(
                                    ext.file_type,
                                    ds,
                                )
    
                                setPopupMessage('You have successfully downloaded the selected dataset.', true);
                            } catch (err:any) {
                                console.log(err);
                                setPopupMessage('There was an error downloading this dataset. Please try again.', false);
                            }
                        }
                    }
                ))
            },
        );
        if (canAccess({or: demo_access})) options.push(
            {
                label: 'Delete',
                btnComp: (
                    <DeleteModal 
                        items={[{
                            type: 'dataset',
                            id,
                            name,
                        }]}
                        deleteFn={deleteDatasets}
                    />
                )
            },
        );

        return options;
    }

    useEffect(() => {
        let options: DropdownOption[] = [];

        if (ds.length > 0 && selectedItems.length > 0) {
            let items: DeleteItemType[] = [];
            let exportItems: DatasetOutput[] = [];
            selectedItems.forEach(id => {
                let matchingItem = ds.find(d => d.file_hash! === id);
                // Also check version cache for expanded version rows
                if (!matchingItem) {
                    for (const versions of Object.values(versionCache)) {
                        const found = versions.find(v => v.file_hash! === id);
                        if (found) { matchingItem = found; break; }
                    }
                }
                if (matchingItem) {
                    items.push({
                        type: 'dataset',
                        id,
                        name: matchingItem.name ? matchingItem.name : ''
                    })

                    exportItems.push(matchingItem);
                }
            });

            const deleteModalComp: DropdownOption = {
                label: 'Delete',
                btnComp: (
                    <DeleteModal 
                        items={items}
                        cancelFn={() => setSelectedItems([])}
                        deleteFn={deleteDatasets}
                    />
                ),
            };

            const exportModalComp: DropdownOption = {
                label: 'Export',
                moreOptions: exportFileExtensions.map(ext => (
                    {
                        label: ext.label,
                        action: async (e: MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                            const promises = exportItems.map(async(exportItem) => {
                                return downloadDataset(
                                    ext.file_type,
                                    exportItem
                                );
                            })
                            try {
                                await Promise.all(promises);
    
                                setPopupMessage('You have successfully downloaded the selected datasets.', true);
                                setSelectedItems([]);
                            } catch (err:any) {
                                console.log(err);
                                setPopupMessage('There was an error downloading the selected datasets. Please try again.', false);
                            }
                        }
                    }
                ))
            };

            options.push(exportModalComp);
            if (canAccess({or: demo_access})) options.push(deleteModalComp);
        };

        setActionDropdownOptions(options);

        //eslint-disable-next-line
    }, [selectedItems, ds, versionCache])

    const toggleGrouped = () => {
        const newGrouped = !isGrouped;
        setExpandedRows(new Set());
        setVersionCache({});
        setSelectedItems([]);
        setParams(prev => ({ ...prev, grouped: newGrouped, offset: 0 }));
    };

    const handleTabChange = (event: React.SyntheticEvent, index: number) => {
        setSelectedIndex(index);
        setExpandedRows(new Set());
        setVersionCache({});

        setParams({
            ...defaultParams,
            dataset_type: (
                tabs[index] === 'Single-Class' ? 'single-class-label' 
                : tabs[index] === 'Continuous-Value' ? 'continuous-value' 
                : tabs[index] === 'Structure Only' ? 'structures-only'
                : 'multi-class-label'
            ) 
        })
        
    }

   


    return (
        <div className="h-screen flex flex-col justify-start items-stretch gap-4 p-10">
            <TrainModelModal
                selectedDatasetId={openTrainModelId ?? undefined}
                setPopupMessage={setPopupMessage}
                isDropdownItem
                open={!!openTrainModelId}
                setOpen={(val) => setOpenTrainModelId(val ? openTrainModelId : null)}
            />

            <BatchPredictionModal 
                selectedDatasetId={openBatchPredictionId ?? undefined}
                setPopupMessage={setPopupMessage}
                isPrimaryMenu={false}
                isHidden
                open={!!openBatchPredictionId}
                setOpen={(val) => setOpenBatchPredictionId(val ? openBatchPredictionId : null)}
            />
        
            <PageHeader label='Datasets'>
                <GenerateDatasetModal 
                    setPopupMessage={setPopupMessage}
                    isDropdownItem={false}
                />
            </PageHeader>

            <div>
                <NewPageTabs 
                    tabs={tabs.map((tab,i) => ({
                        label: tab,
                        index: i
                    }))}
                    selectedIndex={selectedIndex}
                    handleTabChange={handleTabChange}
                />
            </div>

           <div className="flex flex-col justify-start items-start gap-2">
                <div className="w-full flex justify-between items-center gap-4">
                    <div className="max-w-[600px] w-full">
                        <SearchBar
                            searchFn={search}
                            searchKey="name"
                        />
                    </div>
                    <div className="flex justify-end items-center gap-4">
                        {hasAcAccess && (
                            <div className="flex rounded-md border border-gray-300 overflow-hidden text-sm">
                                <button
                                    className={`px-3 py-2.5 transition-colors ${isGrouped ? 'bg-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                    onClick={toggleGrouped}
                                    disabled={isGrouped}
                                >
                                    Grouped
                                </button>
                                <button
                                    className={`px-3 py-2.5 transition-colors ${!isGrouped ? 'bg-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                    onClick={toggleGrouped}
                                    disabled={!isGrouped}
                                >
                                    Flat
                                </button>
                            </div>
                        )}
                        <ColumnsButton 
                            headers={tableHeaders} 
                            setHeaders={setTableHeaders} 
                            isDisabled={false}
                        />
                        {selectedItems.length > 0 && (
                            <NestedDropdown 
                                dir="right"
                                btn={{
                                    label: `Actions (${selectedItems.length} selected)`,
                                    type: 'secondary',
                                    styling: 'solid',
                                    id: 'ds-multi-action-btn',
                                }}
                                options={actionDropdownOptions}
                            />
                        )}
                    </div>
                </div>
                <FilterBar 
                    filters={filters}
                    setParams={setParams}
                    params={params}
                />
            </div>

            <VirtualizedDataTable 
                isLoading={getDatasetMutation.isLoading} 
                paramValues={Object.entries(params).filter(entry => ['name', 'start_date', 'end_date'].includes(entry[0]) && entry[1]).map(val => val[1])}
                headers={tableHeaders} 
                tableData={flattenedTableData}
                selection={{
                    selectedItems,
                    setSelectedItems
                }}
                sorting={{
                    sortableHeaders: ['name', 'created_by', 'owner', 'creation_date', 'updated_date'],
                    sortHeaders: sort,
                    sortByParam: params.sort_by!,
                    sortOrderParam: params.sort_order!,
                }}
                setPopupMessage={setPopupMessage}
                fixedItemHeight={72}
                dataType="datasets"
                count={count}
                expandable={isGrouped}
                expandedRows={expandedRows}
                onToggleExpand={handleToggleExpand}
            />

            <UpdatedPagination 
                count={count}
                limit={params.limit!}
                offset={params.offset!}
                setPaginationLimit={setPaginationLimit}
                setPaginationOffset={setPaginationOffset}
                size="reg"
                isLoading={getDatasetMutation.isLoading}
            />            

            <SnackBar open={open} setOpen={setOpen} message={message} isSuccess={isSuccess}/> 
       
        </div>
    )
}